import { CustomerMapper } from "@omni/mappers/customer/customer.mapper";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductImageRendition } from "@omni/types/api/product/shared/enums/productImageRendition.enum";
import { Payment } from "@omni/types/components/payment/payment.model";
import { PaymentIssuerCode } from "@omni/types/components/payment/shared/enum/paymentIssuerCode.enum";
import { ModelMapper } from "@omni/types/utils/modelMapper";
import { pick } from "lodash-es";

import { OrderCustomerModel } from "@/redux/modules/api/data/orderDetail/@types/orderCustomer.model";
import { OrderCustomerApiModel } from "@/types/service/cart/orders/orderCustomer.api.model";
import { OrderDetailApiModel } from "@/types/service/cart/orders/orderDetail.api.model";
import { OrderDetailsApiModel } from "@/types/service/cart/orders/orderDetails.api.model";
import { OrderItemApiModel } from "@/types/service/cart/orders/orderItem.api.model";
import { PaymentApiModel } from "@/types/service/cart/orders/payment.api.model";
import { Config } from "@/utils/config.utils";
import { productConfigurations } from "@/utils/productConfiguration/productConfiguration.utils";

import { translateWithoutContext } from "../../../value/translations/translations.helpers";
import { OrderDetailModel } from "./@types/orderDetail.model";
import { OrderDetailsModel } from "./@types/orderDetails.model";
import { OrderItemModel } from "./@types/orderItem.model";

const getTitle = (item: OrderItemApiModel) => {
    if (item.articleConfiguration) {
        return item.articleConfiguration.title || "";
    }

    const name = item.product?.productTitle;
    if (name === null || item.warrantyDeposit) {
        return translateWithoutContext("general.product.pallet.title");
    }

    return name;
};

const getUrl = (item: OrderItemApiModel) => {
    if (item.product?.giftCardArticle) {
        return Config.pages.giftcard;
    } else if (item.articleConfiguration) {
        return undefined;
    }

    return item.product?.url;
};

const isAdditionalServiceItem = (
    id: string | null,
    items: OrderItemApiModel[]
) => {
    return (
        !!id &&
        items.some((i) => i.additionalServices?.map((as) => as.id).includes(id))
    );
};

const OrderItemMapper =
    (
        items: OrderItemApiModel[]
    ): ModelMapper<OrderItemApiModel, OrderItemModel> =>
    (model) => {
        return {
            id: model.id,
            deliveryType: model.deliveryType as DeliveryType,
            articleConfiguration: model.articleConfiguration,
            free: model.free,
            totalPrice: model.totalPrice,
            medias: model.articleConfiguration
                ? [
                      {
                          url: model.articleConfiguration.imageURL || "",
                          format: ProductImageRendition.SM,
                          altText: getTitle(model)
                      }
                  ]
                : model.product?.mainImages?.[0].medias,
            title: getTitle(model),
            url: getUrl(model),
            warrantyDeposit: model.warrantyDeposit,
            quantity: model.quantity,
            status: model.orderLineStatuses,
            transportPackageStatuses: model.transportPackageStatuses || [],
            sku: model.sku,
            ean: model.product?.ean,
            boxProduct: model.boxEntry || model.tellerEntry,
            boxLogisticInformation: model.product?.boxLogisticInformation,
            showStrippedPrice: !!model.strikethroughPrice,
            deliveryPromise: model.deliveryPromise,
            strikethroughPrice: model.strikethroughPrice,
            unitPrice: model.unitPrice,
            analytics: model.product?.analytics || null,
            hasProduct: !!model.product,
            brandName: model.product?.brandName,
            configuration: pick(
                model,
                productConfigurations.reduce(
                    (acc, pc) => [...acc, ...pc.configPaths],
                    [] as string[]
                )
            ),
            additionalServices: model.additionalServices,
            isAdditionalService: isAdditionalServiceItem(model.id, items)
        };
    };

const OrderCustomerMapper: ModelMapper<
    OrderCustomerApiModel,
    OrderCustomerModel
> = (model) => {
    return {
        firstName: model.firstName,
        lastName: model.lastName,
        phone: model.phone,
        isB2BClient: !!model.btwNumber && !!model.companyName,
        guest: model.guest,
        vatNumber: model.btwNumber,
        companyName: model.companyName,
        email: model.email
    };
};

export const OrderDetailsMapper: ModelMapper<
    OrderDetailsApiModel,
    OrderDetailsModel
> = (model) => {
    return {
        orderId: model.orderId,
        reservationToken: model.reservationToken,
        creationDate: model.creationDate,
        customer: OrderCustomerMapper(model.customer),
        billingAddress: model.billingAddress,
        deliveryAddress: model.deliveryAddress,
        invoiceUrl: model.pdfInvoiceUrl,
        store: model.store,
        payments: model.payments.map(PaymentMapper),
        items: model.items.map((i) => OrderItemMapper(model.items)(i)),
        deliveryCost: model.deliveryCost,
        hasGiftCardPayments: (model.giftCardPayments || [])?.length > 0,
        paidByGiftCards: model.paidByGiftCards || 0,
        showDeliveryCost: model.items.some(
            (i) => i.deliveryType === DeliveryType.HomeDelivery
        ),
        subTotal: model.subTotalPrice,
        totalPrice: model.totalPrice,
        supplierCount: model.supplierCount,
        transportPossible: model.transportPossible,
        reservation: model.reservation,
        promotions: model.promotions,
        globalDiscounts: model.globalDiscounts,
        bonusCardNumber: model.bonusCardNumber,
        sessionCustomer: model.sessionCustomer
            ? CustomerMapper.fromCloudToComponents(Config.getLocale())(
                  model.sessionCustomer
              )
            : undefined
    };
};

export const OrderDetailMapper: ModelMapper<
    OrderDetailApiModel,
    OrderDetailModel
> = (model) => {
    return {
        errors: model.errors,
        warnings: model.errors,
        order: OrderDetailsMapper(model.order)
    };
};

const PaymentMapper: ModelMapper<PaymentApiModel, Payment> = (model) => ({
    amount: model.amount,
    paymentMethodCode: model.paymentMethodCode,
    issuer: Object.values(PaymentIssuerCode).find(
        (paymentIssuerCode) => paymentIssuerCode === model.issuer
    )
});
